import axios from "axios";
import { defineStore } from "pinia";
import type { UserSubscriptionResponse } from "~/types/payment";
import { useAuthStore } from "~/stores/auth";

export const useCurrentSubscriptionStore = defineStore(
  "currentSubscription",
  () => {
    const authStore = useAuthStore();
    const api = useApi();

    const loading = ref(false);
    const loadAttempted = ref(false);
    const loadedUserId = ref<number | null>(null);
    const subscriptionState = ref<UserSubscriptionResponse | null>(null);

    const subscription = computed(() => {
      if (loadedUserId.value !== authStore.user.id) {
        return null;
      }

      return subscriptionState.value;
    });

    const loaded = computed(() => {
      return loadAttempted.value && loadedUserId.value === authStore.user.id;
    });

    const clear = () => {
      loading.value = false;
      loadAttempted.value = false;
      loadedUserId.value = null;
      subscriptionState.value = null;
    };

    const fetch = async ({ force = false }: { force?: boolean } = {}) => {
      if (!authStore.profileLoaded || !authStore.user.permissions.isTeacher) {
        clear();
        return null;
      }

      if (!force && (loading.value || loaded.value)) {
        return subscription.value;
      }

      loading.value = true;

      try {
        const response = await api.payment.getUserSubscription();
        subscriptionState.value = response;
        loadAttempted.value = true;
        loadedUserId.value = authStore.user.id;
        return response;
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          subscriptionState.value = null;
          loadAttempted.value = true;
          loadedUserId.value = authStore.user.id;
          return null;
        }

        throw error;
      } finally {
        loading.value = false;
      }
    };

    const ensureLoaded = async () => {
      if (loaded.value) {
        return subscription.value;
      }

      return await fetch();
    };

    return {
      loading,
      loaded,
      subscription,
      clear,
      fetch,
      ensureLoaded,
    };
  },
);
